import { useEffect, useMemo } from "react"
import { useEditor, EditorContent } from "@tiptap/react"
import { Extension, Node, mergeAttributes } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import { Bold, Italic, Heading3, Undo2, Redo2 } from "lucide-react"
import { cn } from "@/lib/utils"

// The Takeaway box: <aside class="takeaway"> with paragraphs inside.
const Takeaway = Node.create({
  name: "takeaway",
  group: "block",
  content: "paragraph+",
  defining: true,
  parseHTML: () => [{ tag: "aside" }],
  renderHTML: ({ HTMLAttributes }) => ["aside", mergeAttributes(HTMLAttributes, { class: "takeaway" }), 0],
})

// The Scripture reference under a verse: <cite>John 10:27</cite>.
const Cite = Node.create({
  name: "cite",
  group: "block",
  content: "text*",
  parseHTML: () => [{ tag: "cite" }],
  renderHTML: ({ HTMLAttributes }) => ["cite", HTMLAttributes, 0],
})

// A blockquote keeps its class: "scripture" or "pull-quote".
const BlockquoteClass = Extension.create({
  name: "blockquoteClass",
  addGlobalAttributes: () => [
    {
      types: ["blockquote"],
      attributes: {
        class: {
          default: null,
          parseHTML: (element) => element.getAttribute("class"),
          renderHTML: (attributes) => (attributes.class ? { class: attributes.class } : {}),
        },
      },
    },
  ],
})

function ToolButton({ onClick, active, label, children }) {
  return (
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn("rounded p-1.5 text-zinc-600 hover:bg-zinc-100", active && "bg-zinc-200 text-zinc-900")}
    >
      {children}
    </button>
  )
}

// One section of a chapter (or the author letter) as a small editor. The
// section's heading is not in here: the template owns it. `letter` allows
// paragraphs only.
export function BookSectionEditor({ value, onChange, letter = false, className }) {
  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: letter ? false : { levels: [3] },
      blockquote: letter ? false : {},
      bulletList: letter ? false : {},
      orderedList: false,
      listItem: letter ? false : {},
      codeBlock: false,
      code: false,
      horizontalRule: false,
      strike: false,
      link: false,
      underline: false,
    }),
    ...(letter ? [] : [BlockquoteClass, Takeaway, Cite]),
  ], [letter])

  const editor = useEditor({
    extensions,
    content: value,
    immediatelyRender: false,
    editorProps: { attributes: { class: "min-h-[3rem] focus:outline-none" } },
  })

  useEffect(() => {
    if (!editor || !onChange) return
    const handleUpdate = () => onChange(editor.getHTML())
    editor.on("update", handleUpdate)
    return () => editor.off("update", handleUpdate)
  }, [editor, onChange])

  if (!editor) return <div className={cn("min-h-[3rem] animate-pulse rounded bg-zinc-50", className)} />

  return (
    <div className={cn("rounded-md border border-dashed border-zinc-300 focus-within:border-kf-red", className)}>
      <div className="flex items-center gap-0.5 border-b border-zinc-200 bg-zinc-50 px-1 py-0.5" role="toolbar" aria-label="Text tools">
        <ToolButton label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolButton>
        <ToolButton label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolButton>
        {!letter && (
          <ToolButton label="Sub-head" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
            <Heading3 className="h-4 w-4" />
          </ToolButton>
        )}
        <span className="mx-1 h-4 w-px bg-zinc-200" />
        <ToolButton label="Undo" onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 className="h-4 w-4" />
        </ToolButton>
        <ToolButton label="Redo" onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 className="h-4 w-4" />
        </ToolButton>
      </div>
      <div className="kf-book-page px-3 py-2">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
